import {useMemo, type CSSProperties} from "react";
import {Link, useLocation} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faLock, faArrowLeft, faMedal, faTrophy} from "@fortawesome/free-solid-svg-icons";
import {ExplorerHero, ExplorerHeroStat, ExplorerHeroStats} from "../../../../components/explorer-hero";
import {getRandomDashboardHeroImage} from "../../../../helpers/dashboardHeroImages";
import {buildAchievementSetDetail, formatAchievementDate, formatAchievementValue, getAchievementSetDefinitionById} from "../helpers";
import AchievementDataPointSidebar from "./data-point-sidebar";
import type {AchievementGroupDetailViewProps} from "./types";
import '../achievements.scss';
import './group-detail.scss';

export default function AchievementGroupDetailView(props: AchievementGroupDetailViewProps) {
    const {rawData, lifeList, observationsByLocation, checklist} = props;
    const location = useLocation();
    const pathPrefix = '/discover/achievements/';
    const setId = location.pathname.startsWith(pathPrefix)
        ? decodeURIComponent(location.pathname.slice(pathPrefix.length))
        : '';
    const detail = useMemo(
        () => setId ? buildAchievementSetDetail(setId, {rawData, lifeList, observationsByLocation, checklist}) : undefined,
        [setId, rawData, lifeList, observationsByLocation, checklist]
    );
    const heroImage = useMemo(() => getRandomDashboardHeroImage(), []);

    if (!detail) {
        return (
            <div className="discover achievements achievement-group-detail">
                <Link className="achievement-group-detail__back" to="/discover/achievements">
                    <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
                    Back to Achievements
                </Link>
                <section className="achievements__panel">
                    <h1>Achievement Group Not Found</h1>
                </section>
            </div>
        );
    }

    return (
        <div
            className="discover achievements achievement-group-detail"
            style={{'--achievement-color': detail.set.color} as CSSProperties}
        >
            <Link className="achievement-group-detail__back" to="/discover/achievements">
                <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
                Back to Achievements
            </Link>

            <ExplorerHero
                className="achievement-group-detail__hero"
                backgroundImage={heroImage}
                eyebrow="Achievement Group"
                title={detail.set.title}
                intro={detail.set.description}
            >
                <ExplorerHeroStats ariaLabel={`${detail.set.title} summary`}>
                    <ExplorerHeroStat
                        icon={<FontAwesomeIcon icon={faTrophy} />}
                        label="Levels Earned"
                        value={`${detail.set.earnedCount} / ${detail.set.totalCount}`}
                    />
                    <ExplorerHeroStat
                        icon={<FontAwesomeIcon icon={faMedal} />}
                        label="Total Points"
                        value={formatAchievementValue(detail.totalPoints)}
                    />
                </ExplorerHeroStats>
            </ExplorerHero>

            <div className={`achievement-group-detail__content ${detail.dataPoints ? 'achievement-group-detail__content--with-sidebar' : ''}`.trim()}>
                <section className="achievement-group-detail__levels" aria-labelledby="achievement-group-levels-title">
                    <h2 id="achievement-group-levels-title" className="achievement-group-detail__heading">{detail.set.title} Levels</h2>
                    <div className="achievement-group-detail__milestones">
                        {detail.milestones.map(milestone => {
                            const setDefinition = getAchievementSetDefinitionById(detail.set.id);
                            const statusLabel = milestone.earned
                                ? 'Earned'
                                : `${milestone.percent}% complete`;

                            return (
                            <article
                                className={`achievement-group-detail__milestone ${milestone.earned ? 'is-earned' : 'is-locked'}`.trim()}
                                key={milestone.id}
                                style={{'--achievement-color': detail.set.color} as CSSProperties}
                            >
                                <div className="achievement-group-detail__milestone-badge">
                                    <div className="achievement-hex" style={{'--achievement-color': detail.set.color} as CSSProperties} aria-hidden="true">
                                        <FontAwesomeIcon icon={milestone.earned ? detail.set.icon : faLock} />
                                    </div>
                                </div>
                                <div className="achievement-group-detail__milestone-copy">
                                    <span className="achievement-group-detail__milestone-kicker">{setDefinition?.title ?? detail.set.title}</span>
                                    <div className="achievement-group-detail__milestone-title-row">
                                        <strong className="achievement-group-detail__milestone-title">{milestone.title}</strong>
                                    </div>
                                    {milestone.earnedDate ? (
                                        <span className="achievement-group-detail__milestone-date">Earned {formatAchievementDate(milestone.earnedDate)}</span>
                                    ) : null}
                                    {milestone.unlockContext ? (
                                        <div className="achievement-group-detail__milestone-context">
                                            Unlocked by{' '}
                                            {milestone.unlockContext.primaryDetailPath ? (
                                                <Link to={milestone.unlockContext.primaryDetailPath}>
                                                    <strong>{milestone.unlockContext.primaryLabel}</strong>
                                                </Link>
                                            ) : <strong>{milestone.unlockContext.primaryLabel}</strong>}
                                            {milestone.unlockContext.secondaryLabel ? (
                                                <>
                                                    {' '}at{' '}
                                                    {milestone.unlockContext.secondaryDetailPath ? (
                                                        <Link to={milestone.unlockContext.secondaryDetailPath}>
                                                            <strong>{milestone.unlockContext.secondaryLabel}</strong>
                                                        </Link>
                                                    ) : <strong>{milestone.unlockContext.secondaryLabel}</strong>}
                                                </>
                                            ) : null}
                                        </div>
                                    ) : (
                                        <span className="achievement-group-detail__milestone-status">{statusLabel}</span>
                                    )}
                                    {!milestone.earned ? (
                                        <div
                                            className="achievement-group-detail__milestone-progress achievement-progress"
                                            style={{'--achievement-progress': `${milestone.percent}%`, '--achievement-color': detail.set.color} as CSSProperties}
                                        >
                                            <span />
                                        </div>
                                    ) : null}
                                </div>
                            </article>
                            );
                        })}
                    </div>
                </section>

                {detail.dataPoints ? (
                    <AchievementDataPointSidebar title={detail.dataPointTitle ?? 'Data Counted'} dataPoints={detail.dataPoints} />
                ) : null}
            </div>
        </div>
    );
}
